import { useContext, useRef, useState } from 'react'
import { HeadFC, graphql } from 'gatsby'

import Seo from '@src/components/Seo'
import ArtisanLayout from '@src/components/Artisan/ArtisanLayout'
import HeroSection from '@src/components/Artisan/HeroSection'
import MapSection from '@src/components/Artisan/MapSection'
import InstagramSection from '@src/components/Artisan/InstagramSection'
import ArticlesSection from '@src/components/Artisan/ArticlesSection'
import ArtisanSplash from '@src/components/Artisan/ArtisanSplash'
import ArtisanColumn from '@src/components/Artisan/ArtisanColumn'
import VideoSection from '@src/components/Artisan/VideoSection'
import { Article, useAllArticles } from '@models/Article'
import { useTranslation } from '@models/Translation'
import { useAllArtisans } from '@models/Artisan/useAllArtisans'
import { StoreContext } from '@models/Store'
import { Video } from '@src/models/Video'

export type ArtisanPageHeading =
  | 'video'
  | 'location'
  | 'articles'
  | 'column'
  | 'instagram'

export type ArtisanArticle = {
  article: Article
  location: string
}

type ArtisanPageProps = {
  data: {
    allArtisanCarousel: {
      nodes: {
        blogId: string
      }[]
    }
    allVideo: {
      nodes: Video[]
    }
  }
}
const ArtisanPage = ({ data }: ArtisanPageProps) => {
  const { locale } = useTranslation()
  const { app } = useContext(StoreContext)
  const [showSplash] = useState(app.loading)
  const allArtisans = useAllArtisans()
  const artisanArticles: ArtisanArticle[] = useAllArticles()
    .map(article => {
      const artisan = allArtisans.find(artisan =>
        artisan.blogIds.includes(article.blogId),
      )
      if (!artisan) return null

      return {
        article,
        location:
          locale === 'ja'
            ? `${artisan.prefecture} ${artisan.city}`
            : `${artisan.cityEn}, ${artisan.prefectureEn}`,
      }
    })
    .filter((result): result is ArtisanArticle => result !== null)
  const carouselIds = data.allArtisanCarousel.nodes.map(node => node.blogId)

  const videoElement = useRef<HTMLDivElement | null>(null)
  const locationElement = useRef<HTMLDivElement | null>(null)
  const articlesElement = useRef<HTMLDivElement | null>(null)
  const columnElement = useRef<HTMLDivElement | null>(null)
  const instagramElement = useRef<HTMLDivElement | null>(null)

  const scrollToHeadings = (heading: ArtisanPageHeading) => {
    let targetElement: React.RefObject<HTMLDivElement | null>
    switch (heading) {
      case 'video':
        targetElement = videoElement
        break
      case 'location':
        targetElement = locationElement
        break
      case 'articles':
        targetElement = articlesElement
        break
      case 'column':
        targetElement = columnElement
        break
      case 'instagram':
        targetElement = instagramElement
        break
    }
    targetElement.current?.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <ArtisanLayout handleClickHeading={scrollToHeadings} enableDissolve={false}>
      <HeroSection
        artisanArticles={artisanArticles}
        carouselIds={carouselIds}
      />
      <VideoSection allVideos={data.allVideo.nodes} ref={videoElement} />
      <MapSection
        artisans={allArtisans}
        artisanArticles={artisanArticles}
        ref={locationElement}
      />
      <ArticlesSection ref={articlesElement} />
      <ArtisanColumn ref={columnElement} />
      <InstagramSection ref={instagramElement} />
      {showSplash && <ArtisanSplash />}
    </ArtisanLayout>
  )
}

const mapboxTags = (
  <>
    <link
      href="https://api.mapbox.com/mapbox-gl-js/v3.2.0/mapbox-gl.css"
      rel="stylesheet"
    />
  </>
)

export const Head: HeadFC = ({ location }) => {
  return (
    <>
      <ArtisanDefaultSeo title="Artisan" pathName={location.pathname} />
      {mapboxTags}
    </>
  )
}

export const ArtisanDefaultSeo = ({
  title,
  pathName,
}: {
  title: string
  pathName: string
}) => {
  const locale = pathName.startsWith('/en') ? 'en' : 'ja'

  return (
    <Seo
      pathName={pathName}
      title={title}
      description={
        locale === 'ja'
          ? '「Artisan」は、日本の工芸の「美」・「技術」・「職人」の魅力を未来へと紡いでいくプロジェクトです。'
          : '"Artisan" is a project that weaves the ’beauty’, ’technique’, and charm of ’artisans’ and their crafts into the future.'
      }
      imageUrl="https://fashiontechnews.zozo.com/artisan_ogp.jpg"
    />
  )
}

export default ArtisanPage

export const query = graphql`
  {
    allArtisanCarousel {
      nodes {
        blogId
      }
    }
    allVideo(filter: { category: { eq: "Artisan" } }) {
      nodes {
        videoId
        title
        titleEn
        category
        tags
        tagsEn
      }
    }
  }
`
